import { useId, useState, type CSSProperties } from 'react';
import { Building2, Check, Layers, MessageSquare, Table2, UserRound, Users } from 'lucide-react';
import { Button, Logo } from '@vd/ui';
import type {
  BillingInterval,
  BillingPlanDefinition,
  BillingPlansResponse,
  SelfServicePlan,
} from '@vd/types';
import type { SubscriptionState } from './useBillingSummary';
import { dollars } from './billingDisplay';
import { WidgetUsage } from './WidgetUsage';
import styles from './Billing.module.css';

export const savingPercent = (price: { monthlyCents: number; annualCents: number }) =>
  price.monthlyCents > 0
    ? Math.max(0, (1 - price.annualCents / (price.monthlyCents * 12)) * 100)
    : 0;
const percent = (value: number) =>
  `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
const highlightIds = [
  'tickets',
  'recording',
  'narration',
  'storage',
  'retention',
  'reports',
  'mcp',
];
type Actions = {
  choose: (plan: SelfServicePlan) => void;
  manage: () => void;
  contact: () => void;
  free: () => void;
};
interface Props {
  sub: SubscriptionState;
  details: BillingPlansResponse | undefined;
  loading: boolean;
  error: boolean;
  retry: () => void;
  interval: BillingInterval;
  setInterval: (value: BillingInterval) => void;
  isOwner: boolean;
  busy: boolean;
  companyId: string;
  actions: Actions;
  widgetOpen: boolean;
  setWidgetOpen: (value: boolean) => void;
  changed: () => void;
}
export function BillingPlans({
  sub,
  details,
  loading,
  error,
  retry,
  interval,
  setInterval,
  isOwner,
  busy,
  companyId,
  actions,
  widgetOpen,
  setWidgetOpen,
  changed,
}: Props) {
  const savingsId = useId();
  const maxSaving = details
    ? Math.max(0, ...details.plans.filter((plan) => plan.available).map(savingPercent))
    : 0;
  return (
    <>
      <div className={styles.sectionHeading}>
        <div>
          <h2>
            <span className={`${styles.iconTile} ${styles.blue}`}>
              <Layers aria-hidden="true" size={18} />
            </span>
            A plan for the way you work
          </h2>
          <p className={styles.muted}>
            Recording and MCP are included across plans, with applicable limits.
          </p>
        </div>
        <fieldset className={styles.interval}>
          <legend className={styles.srOnly}>Offer billing interval</legend>
          {(['monthly', 'annual'] as const).map((value) => (
            <label key={value}>
              <input
                type="radio"
                name="billing-interval"
                aria-label={value === 'annual' ? 'Annual' : 'Monthly'}
                aria-describedby={value === 'annual' ? savingsId : undefined}
                checked={interval === value}
                onChange={() => setInterval(value)}
              />
              {value === 'annual' ? 'Annual' : 'Monthly'}
              {value === 'annual' && (
                <span id={savingsId} className={styles.saving} hidden={maxSaving <= 0}>
                  Save up to {percent(maxSaving)}
                </span>
              )}
            </label>
          ))}
        </fieldset>
      </div>
      {loading && (
        <p role="status" className={styles.muted}>
          Loading plan details…
        </p>
      )}
      {error && (
        <div className={`${styles.notice} ${styles.warning}`} role="status">
          Plan details are temporarily unavailable. Your current subscription is unchanged.{' '}
          <button className={styles.textLink} onClick={retry}>
            Retry plan details
          </button>
        </div>
      )}
      {details && (
        <>
          <div
            className={styles.planGrid}
            style={{ '--plan-count': details.plans.length } as CSSProperties}
          >
            {details.plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                rows={details.rows}
                sub={sub}
                interval={interval}
                isOwner={isOwner}
                busy={busy}
                actions={actions}
              />
            ))}
          </div>
          {isOwner && ['personal', 'team', 'business'].includes(sub.plan) && (
            <div className={styles.freeLink}>
              <button className={styles.textLink} onClick={actions.free} disabled={busy}>
                {sub.cancelAtPeriodEnd ? 'Review scheduled cancellation' : 'Switch to Free'}
              </button>
            </div>
          )}
          <p className={styles.footnote}>
            Free remains available. Enterprise is handled through a contract. Member allowances are
            pooled on Team and Business; widgets are per workspace.
          </p>
        </>
      )}
      <details
        id="billing-widget-capacity"
        className={`${styles.disclosure} ${styles.widgetSection} ${styles.magenta}`}
        open={widgetOpen}
        onToggle={(event) => setWidgetOpen(event.currentTarget.open)}
      >
        <summary>
          <span>
            <span className={styles.summaryTitle}>
              <span className={styles.iconTile}>
                <MessageSquare aria-hidden="true" size={18} />
              </span>
              Widget capacity
            </span>
            <span className={styles.summaryCopy}>
              Start with what is included. Estimate your monthly report volume.
            </span>
          </span>
          <span className={styles.disclosureAction}>Explore options +</span>
        </summary>
        <div className={styles.disclosureBody}>
          {details &&
            sub.widget &&
            !sub.widget.tierOnly &&
            sub.widget.reportsAllowance !== null && (
              <WidgetEstimate details={details} sub={sub} interval={interval} />
            )}
          {sub.widget?.reportsAllowance === null && (
            <p className={styles.footnote}>
              Your report capacity is unlimited. Review visitor recording capacity and any existing
              widget subscription below.
            </p>
          )}
          {sub.widget ? (
            <WidgetUsage
              widget={sub.widget}
              isOwner={isOwner}
              companyId={companyId}
              billingConfigured={sub.billingConfigured !== false}
              onChanged={changed}
              presentation="plans"
              interval={interval}
              displayTiers={details?.widgetTiers}
            />
          ) : (
            <p className={styles.footnote}>
              Optional widget tiers are available on paid plans. Choose a plan to add widget
              capacity.
            </p>
          )}
        </div>
      </details>
      {details && (
        <details className={`${styles.disclosure} ${styles.comparisonSection} ${styles.cyan}`}>
          <summary>
            <span>
              <span className={styles.summaryTitle}>
                <span className={styles.iconTile}>
                  <Table2 aria-hidden="true" size={18} />
                </span>
                Compare all plan features
              </span>
              <span className={styles.summaryCopy}>
                See included capacity and how each plan works, side by side.
              </span>
            </span>
            <span className={styles.disclosureAction}>Show comparison +</span>
          </summary>
          <div className={styles.disclosureBody}>
            <p className={styles.footnote}>
              Member allowances are per internal member and pooled on Team and Business. Widget
              allowances are per workspace. Recording minutes reset monthly even on annual plans;
              storage does not reset monthly.
            </p>
            <div
              role="region"
              aria-label="Plan feature comparison, scroll horizontally"
              tabIndex={0}
              className={styles.tableScroll}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    {details.plans.map((plan) => (
                      <th scope="col" key={plan.id}>
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.rows.map((row, index) => (
                    <FeatureRows
                      key={row.id}
                      row={row}
                      group={index === 0 || details.rows[index - 1]?.group !== row.group}
                      plans={details.plans}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      )}
      <div className={styles.enterprise}>
        <div>
          <h3 className={styles.sectionTitle}>
            <span className={`${styles.iconTile} ${styles.amber}`}>
              <Building2 aria-hidden="true" size={18} />
            </span>
            Need a contract or a larger setup?
          </h3>
          <p className={styles.muted}>Tell us about your team and requirements.</p>
        </div>
        <Button variant="secondary" onClick={actions.contact}>
          Contact us
        </Button>
      </div>
    </>
  );
}
function PlanCard({
  plan,
  rows,
  sub,
  interval,
  isOwner,
  busy,
  actions,
}: {
  plan: BillingPlanDefinition;
  rows: BillingPlansResponse['rows'];
  sub: SubscriptionState;
  interval: BillingInterval;
  isOwner: boolean;
  busy: boolean;
  actions: Actions;
}) {
  const current = plan.id === sub.plan;
  const Icon = plan.id === 'personal' ? UserRound : plan.id === 'team' ? Users : Building2;
  const tone =
    plan.id === 'personal' ? styles.blue : plan.id === 'team' ? styles.green : styles.amber;
  const amount = interval === 'annual' ? plan.annualCents / 12 : plan.monthlyCents;
  return (
    <article className={`${styles.planCard} ${current ? styles.current : ''}`}>
      {current && (
        <span className={styles.frog} aria-hidden="true">
          <Logo size={60} />
        </span>
      )}
      <div className={styles.planTitle}>
        <h3>
          <span className={`${styles.iconTile} ${tone}`}>
            <Icon aria-hidden="true" size={18} />
          </span>
          {plan.name}
        </h3>
        {current && (
          <span className={styles.badge}>
            Current{sub.planInterval ? ` · ${sub.planInterval}` : ''}
          </span>
        )}
      </div>
      <p className={styles.audience}>{plan.audience}</p>
      <div className={styles.price}>{dollars(amount)}</div>
      <div className={styles.priceUnit}>
        {plan.perSeat ? 'per seat / month' : 'per month'}
        {interval === 'annual' ? ', equivalent' : ''}
      </div>
      <div className={styles.billingNote}>
        {dollars(interval === 'annual' ? plan.annualCents : plan.monthlyCents)}
        {plan.perSeat ? ' / seat' : ''} billed {interval === 'annual' ? 'yearly' : 'monthly'}
        {interval === 'annual' && savingPercent(plan) > 0 && (
          <>
            <br />
            <span className={styles.saving}>Save {percent(savingPercent(plan))}</span>
          </>
        )}
        {plan.minSeats > 1 && (
          <>
            <br />
            Minimum {plan.minSeats} seats:{' '}
            {dollars(
              (interval === 'annual' ? plan.annualCents : plan.monthlyCents) * plan.minSeats,
            )}{' '}
            / {interval === 'annual' ? 'year' : 'month'}
          </>
        )}
      </div>
      <ul className={styles.features}>
        {highlightIds.map((id) => {
          const row = rows.find((item) => item.id === id);
          return row ? (
            <li key={id}>
              <Check aria-hidden="true" size={13} />
              <span>
                {row.label}: {plan.values[id]}
              </span>
            </li>
          ) : null;
        })}
      </ul>
      {isOwner ? (
        <Button
          variant={current ? 'secondary' : 'primary'}
          disabled={busy || (!current && !plan.available)}
          onClick={() => (current ? actions.manage() : actions.choose(plan.id))}
        >
          {busy
            ? 'Please wait…'
            : current
              ? 'Manage current plan'
              : sub.plan === 'enterprise'
                ? 'Contact us'
                : !plan.available
                  ? 'Currently unavailable'
                  : `${sub.plan === 'free' ? 'Choose' : 'Switch to'} ${plan.name}`}
        </Button>
      ) : (
        <p className={styles.muted}>Only the workspace owner can change the plan.</p>
      )}
    </article>
  );
}
function FeatureRows({
  row,
  group,
  plans,
}: {
  row: BillingPlansResponse['rows'][number];
  group: boolean;
  plans: BillingPlanDefinition[];
}) {
  return (
    <>
      {group && (
        <tr className={styles.group}>
          <th colSpan={plans.length + 1}>{row.group}</th>
        </tr>
      )}
      <tr>
        <th scope="row">{row.label}</th>
        {plans.map((plan) => (
          <td key={plan.id}>{plan.values[row.id] ?? 'Not specified'}</td>
        ))}
      </tr>
    </>
  );
}
function WidgetEstimate({
  details,
  sub,
  interval,
}: {
  details: BillingPlansResponse;
  sub: SubscriptionState;
  interval: BillingInterval;
}) {
  const widget = sub.widget!;
  const [volume, setVolume] = useState(2500);
  const [draft, setDraft] = useState('2500');
  const id = useId();
  const base = details.widgetBase.reports;
  const maximum = Math.max(
    10_000,
    ...details.widgetTiers.map((tier) => (base ?? 0) + tier.reports),
  );
  const invalid =
    !/^\d+$/.test(draft) ||
    !Number.isSafeInteger(Number(draft)) ||
    Number(draft) < 100 ||
    Number(draft) > maximum;
  const covered = widget.reportsAllowance === null || volume <= widget.reportsAllowance;
  const matched =
    base === null
      ? undefined
      : [...details.widgetTiers]
          .sort((a, b) => a.reports - b.reports)
          .find((tier) => base + tier.reports >= volume && tier.available);
  const quoteInterval =
    widget.tierBought || widget.tierHeld ? (widget.tierInterval ?? interval) : interval;
  const update = (value: string) => {
    setDraft(value);
    if (
      /^\d+$/.test(value) &&
      Number.isSafeInteger(Number(value)) &&
      Number(value) >= 100 &&
      Number(value) <= maximum
    )
      setVolume(Number(value));
  };
  return (
    <>
      <div className={styles.widgetEstimator}>
        <div>
          <h3>How many reports do you expect each month?</h3>
          <p className={styles.muted}>
            Current capacity:{' '}
            {widget.reportsAllowance === null
              ? 'unlimited'
              : widget.reportsAllowance.toLocaleString()}{' '}
            reports per month, per workspace.
          </p>
          <label className={styles.rangeLabel} htmlFor={id}>
            Expected monthly reports <output>{volume.toLocaleString()}</output>
          </label>
          <input
            id={id}
            type="range"
            min={100}
            max={maximum}
            step={1}
            value={volume}
            onChange={(event) => update(event.target.value)}
          />
          <div className={styles.rangeEnds}>
            <span>100</span>
            <span>{maximum.toLocaleString()} reports</span>
          </div>
          <label className={styles.numberLabel}>
            Or enter a report count
            <input
              type="number"
              min={100}
              max={maximum}
              step={1}
              value={draft}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? `${id}-error` : undefined}
              onChange={(event) => update(event.target.value)}
            />
          </label>
          {invalid && (
            <p id={`${id}-error`} role="status" className={styles.footnote}>
              Enter a whole number from 100 to {maximum.toLocaleString()}. Showing the last valid
              estimate for {volume.toLocaleString()} reports.
            </p>
          )}
        </div>
        <div className={styles.recommendation} role="status" aria-live="polite">
          {covered ? (
            <>
              <span className={styles.badge}>Already covered</span>
              <h3>Your current report capacity covers this volume</h3>
              <p className={styles.muted}>No extra report tier is needed for this estimate.</p>
            </>
          ) : matched ? (
            <>
              <span className={styles.eyebrow}>Report capacity match</span>
              <h3>Widget {matched.tier.charAt(0).toUpperCase() + matched.tier.slice(1)}</h3>
              <div className={styles.price}>
                {dollars(quoteInterval === 'annual' ? matched.annualCents : matched.monthlyCents)}
              </div>
              <p className={styles.muted}>
                / {quoteInterval === 'annual' ? 'year' : 'month'}, per workspace
                <br />
                {((base ?? 0) + matched.reports).toLocaleString()} total reports / month. Review the
                tier and confirm below.
              </p>
            </>
          ) : (
            <>
              <h3>Contact us about this capacity</h3>
              <p className={styles.muted}>No available widget tier matches this estimate.</p>
            </>
          )}
        </div>
      </div>
      <p className={styles.footnote}>
        Prices change at existing tier boundaries. This estimates report capacity, not a per-report
        charge. Visitor minutes, current usage and existing purchased terms also matter. Changing
        this selector never purchases a tier.
      </p>
    </>
  );
}
